import { FaceLandmarker, FilesetResolver, type FaceLandmarkerResult } from '@mediapipe/tasks-vision';

import type { ActionEvent, ActionType } from './types';
import type { ModelResources } from './runtimeConfig';

/** 原始摄像头归一化坐标，仅用于显示每张被检测人脸的位置，不参与人数过滤。 */
export interface FaceBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface FaceObservation {
  faceCount: number;
  faceBounds: FaceBounds[];
  blinkLeft: number;
  blinkRight: number;
  /** 原始相机变换矩阵的转头角度，单位为度；正值为使用者向自己的左侧转头。 */
  yaw: number;
  faceSize: number;
  centerOffset: number;
  qualityPassed: boolean;
}

export interface LivenessSnapshot {
  currentAction: ActionType | null;
  currentIndex: number;
  totalActions: number;
  completed: boolean;
  message: string;
  observation: FaceObservation;
  events: ActionEvent[];
}

type ActionPhase = 'WAIT_GESTURE' | 'WAIT_NEUTRAL';

// POC 阈值集中维护，真机测试后应按设备分布调整，不能直接视为生产活体参数。
const BLINK_CLOSED_THRESHOLD = 0.48;
const EYE_OPEN_THRESHOLD = 0.25;
const TURN_YAW_THRESHOLD = 14;
const NEUTRAL_YAW_THRESHOLD = 7;
const MIN_FACE_SIZE = 0.25;
const MAX_FACE_SIZE = 0.72;
const MAX_CENTER_OFFSET = 0.24;

const actionLabels: Record<ActionType, string> = {
  BLINK: '眨眼一次',
  TURN_LEFT: '向你的左侧转头',
  TURN_RIGHT: '向你的右侧转头',
};

export class ActionTracker {
  private actions: ActionType[] = [];
  private index = 0;
  private phase: ActionPhase = 'WAIT_GESTURE';
  private startedAt = 0;
  private completedEvents: ActionEvent[] = [];

  start(actions: ActionType[], startedAt = performance.now()): void {
    if (!actions.length) throw new Error('动作序列不能为空');
    this.actions = [...actions];
    this.index = 0;
    this.phase = 'WAIT_GESTURE';
    this.startedAt = startedAt;
    this.completedEvents = [];
  }

  update(observation: FaceObservation, now = performance.now()): LivenessSnapshot {
    const currentAction = this.actions[this.index] ?? null;
    if (!currentAction) return this.snapshot(observation, '动作检测完成');
    if (observation.faceCount !== 1) {
      return this.snapshot(observation, observation.faceCount > 1 ? '画面中只能有一人' : '请将面部移入取景框');
    }
    if (!observation.qualityPassed) {
      return this.snapshot(observation, '请正对镜头并保持合适距离');
    }

    if (currentAction === 'BLINK') {
      // 必须观察到“双眼闭合 -> 双眼重新睁开”的完整变化，避免单帧误判为一次眨眼。
      const closed = observation.blinkLeft >= BLINK_CLOSED_THRESHOLD && observation.blinkRight >= BLINK_CLOSED_THRESHOLD;
      const opened = observation.blinkLeft <= EYE_OPEN_THRESHOLD && observation.blinkRight <= EYE_OPEN_THRESHOLD;
      if (this.phase === 'WAIT_GESTURE' && closed) this.phase = 'WAIT_NEUTRAL';
      if (this.phase === 'WAIT_NEUTRAL' && opened) this.completeCurrent(now);
    } else {
      // 左右以使用者自身为准：原始相机中的正角度对应本人左转，镜像预览中朝屏幕左侧。
      // 不能按未经镜像的图像左右命名动作，否则文字、语音和实际需要转的方向相反。
      const turned = currentAction === 'TURN_LEFT' ? observation.yaw >= TURN_YAW_THRESHOLD : observation.yaw <= -TURN_YAW_THRESHOLD;
      // 转头完成后还要回到中性区，保证最终抓拍是可供服务端比对的正脸。
      const neutral = Math.abs(observation.yaw) <= NEUTRAL_YAW_THRESHOLD;
      if (this.phase === 'WAIT_GESTURE' && turned) this.phase = 'WAIT_NEUTRAL';
      if (this.phase === 'WAIT_NEUTRAL' && neutral) this.completeCurrent(now);
    }

    const nextAction = this.actions[this.index] ?? null;
    const message = nextAction ? (this.phase === 'WAIT_NEUTRAL' ? '很好，请回到正面' : actionLabels[nextAction]) : '动作检测完成';
    return this.snapshot(observation, message);
  }

  private completeCurrent(now: number): void {
    const action = this.actions[this.index];
    if (!action) return;
    this.completedEvents.push({ action, completedAtMs: Math.round(now - this.startedAt) });
    this.index += 1;
    this.phase = 'WAIT_GESTURE';
  }

  private snapshot(observation: FaceObservation, message: string): LivenessSnapshot {
    return {
      currentAction: this.actions[this.index] ?? null,
      currentIndex: this.index,
      totalActions: this.actions.length,
      completed: this.index >= this.actions.length,
      message,
      observation,
      events: [...this.completedEvents],
    };
  }
}

export class LivenessRunner {
  private landmarker: FaceLandmarker | null = null;
  private frameHandle = 0;
  private running = false;
  private lastProcessedAt = 0;
  private lastVideoTime = -1;
  private rejectRun: ((reason?: unknown) => void) | null = null;
  private loadedAssetBase = '';

  constructor(private readonly video: HTMLVideoElement) {}

  async initialize(resources: ModelResources): Promise<void> {
    const resourceKey = JSON.stringify(resources);
    if (this.landmarker && this.loadedAssetBase === resourceKey) return;
    if (this.landmarker) this.close();
    // 云模式使用完整 URL，可部署在任意目录；资源请求不发送平台访问令牌。
    const files = await FilesetResolver.forVisionTasks(resources.wasmBaseUrl).catch(() => {
      throw new Error('动作运行文件加载失败，请检查资源地址、网络和跨域配置');
    });
    const options = {
      baseOptions: {
        modelAssetPath: resources.modelUrl,
        // 手机跨浏览器持续误报多人时，先使用 CPU/WASM 排除 GPU 推理兼容性因素。
        delegate: 'CPU' as const,
      },
      runningMode: 'VIDEO' as const,
      numFaces: 2, // 保留多人检测；不能通过限制只输出一张脸来掩盖误检。
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
      minFaceDetectionConfidence: 0.55,
      minFacePresenceConfidence: 0.55,
      minTrackingConfidence: 0.55,
    };
    try {
      this.landmarker = await FaceLandmarker.createFromOptions(files, options);
    } catch {
      throw new Error('动作模型加载失败，请检查模型地址、网络和跨域配置');
    }
    this.loadedAssetBase = resourceKey;
  }

  async run(
    actions: ActionType[],
    onUpdate: (snapshot: LivenessSnapshot) => void,
    timeoutMs = 18_000,
  ): Promise<LivenessSnapshot> {
    if (!this.landmarker) throw new Error('动作模型尚未加载');
    this.stop();
    const tracker = new ActionTracker();
    const startedAt = performance.now();
    tracker.start(actions, startedAt);
    this.lastVideoTime = -1;
    this.lastProcessedAt = 0;
    this.running = true;

    return await new Promise<LivenessSnapshot>((resolve, reject) => {
      this.rejectRun = reject;
      const tick = () => {
        if (!this.running) {
          this.rejectRun = null;
          reject(new Error('动作检测已取消'));
          return;
        }
        const now = performance.now();
        if (now - startedAt > timeoutMs) {
          this.running = false;
          this.rejectRun = null;
          reject(new Error('动作检测超时，请重新开始'));
          return;
        }
        // 手机相机帧率可能低于定时频率，同一视频帧不能反复送入有状态的视频跟踪器。
        if (now - this.lastProcessedAt >= 66 && this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && this.video.currentTime !== this.lastVideoTime) {
          this.lastProcessedAt = now;
          this.lastVideoTime = this.video.currentTime;
          let result: FaceLandmarkerResult;
          try {
            result = this.landmarker!.detectForVideo(this.video, now);
          } catch {
            // rAF 回调中的推理错误必须结束 Promise，否则页面会一直停在“检测中”。
            this.running = false;
            this.rejectRun = null;
            reject(new Error('动作检测运行失败，请重置后重试'));
            return;
          }
          const observation = toObservation(result);
          const snapshot = tracker.update(observation, now);
          onUpdate(snapshot);
          if (snapshot.completed) {
            this.running = false;
            this.rejectRun = null;
            resolve(snapshot);
            return;
          }
        }
        this.frameHandle = requestAnimationFrame(tick);
      };
      this.frameHandle = requestAnimationFrame(tick);
    });
  }

  stop(): void {
    const reject = this.rejectRun;
    this.rejectRun = null;
    this.running = false;
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    // 外部中止时主动结束 run() 的 Promise，避免页面切后台后调用链永久悬挂。
    reject?.(new Error('动作检测已取消'));
  }

  close(): void {
    this.stop();
    this.landmarker?.close();
    this.landmarker = null;
  }
}

export function toObservation(result: FaceLandmarkerResult): FaceObservation {
  const faceCount = result.faceLandmarks.length;
  // 多人时也保留每组模型输出的包围框，方便辨别预览外人脸、背景误检或重叠框。
  const faceBounds = result.faceLandmarks.filter((points) => points.length > 0).map((points) => ({
    minX: Math.min(...points.map((point) => point.x)),
    maxX: Math.max(...points.map((point) => point.x)),
    minY: Math.min(...points.map((point) => point.y)),
    maxY: Math.max(...points.map((point) => point.y)),
  }));
  const primaryBounds = faceBounds[0];
  if (faceCount !== 1 || !primaryBounds) return emptyObservation(faceCount, faceBounds);
  const categories = result.faceBlendshapes[0]?.categories ?? [];
  const score = (name: string) => categories.find((category) => category.categoryName === name)?.score ?? 0;
  const { minX, maxX, minY, maxY } = primaryBounds;
  const faceSize = Math.min(maxX - minX, maxY - minY);
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const centerOffset = Math.hypot(centerX - 0.5, centerY - 0.5);
  const matrix = result.facialTransformationMatrixes[0]?.data;
  // 保留 Face Landmarker 原始角度：正值本人左转、负值本人右转；CSS 镜像不再次取反。
  const yaw = matrix && matrix.length >= 11 ? (Math.atan2(Number(matrix[8]), Number(matrix[10])) * 180) / Math.PI : 0;
  const qualityPassed = faceSize >= MIN_FACE_SIZE && faceSize <= MAX_FACE_SIZE && centerOffset <= MAX_CENTER_OFFSET;
  return {
    faceCount,
    faceBounds,
    blinkLeft: score('eyeBlinkLeft'),
    blinkRight: score('eyeBlinkRight'),
    yaw,
    faceSize,
    centerOffset,
    qualityPassed,
  };
}

function emptyObservation(faceCount: number, faceBounds: FaceBounds[]): FaceObservation {
  return {
    faceCount,
    faceBounds,
    blinkLeft: 0,
    blinkRight: 0,
    yaw: 0,
    faceSize: 0,
    centerOffset: 1,
    qualityPassed: false,
  };
}
